import EventEmitter, { once } from "events";
import { deviceErrorFromCode } from "../errors";
import {
  DeviceChipType,
  DeviceType,
  UserConfirmationRequest,
  UserConfirmationResponse,
} from "../types";

/* ── EVM coin type (BIP-44) ──────────────────────────────────────────────── */
export const ETHEREUM_COIN_TYPE = 24;

/* ── Enums ───────────────────────────────────────────────────────────────── */
export enum UsbDataType {
  OPEN_PORT_CMD = 0,
  CLOSE_PORT_CMD = 1,
  DATA_CMD = 2,
}

export enum Response {
  SUCCESS = 0,
  ERROR = -1,
  DEVICE_NOT_FOUND = -2,
  PAIRING_FAILED = -3,
}

enum SendDataType {
  FEATURE_DATA = 0,
  INTERRUPT_DATA = 1,
}

enum NotifyType {
  CONNECTED = 0,
  DISCONNECTED = 1,
  SEND_STATUS = 2,
  RECEIVED_DATA = 3,
  RECEIVED_I2C_USB_STATUS = 4,
  RECEIVED_SERVER_DATA = 5,
}

/* ── Module-level WASM / USB state ───────────────────────────────────────── */
export let exportWASM: any = null;
export let memoryBuffer: any = null;
export let emitter: any;
export let res: { data: Array<number> | any | undefined; error: any } = {
  data: undefined,
  error: undefined,
};

export let SendBuffer: any = null;
export let responseBuffer: any = null;
export let pathBuffer: any = null;
export let txBuffer: any = null;
export let tokenBuffer: any = null;
export let walletIdBuffer: any = null;
export let readFeatureReportBuffer: any = null;
export let responseQueue: any = null;

export let SELECTEDDEVICE: HIDDevice | undefined = undefined;
export let deviceChipType: DeviceChipType = DeviceChipType.SINGLE_CHIP;
export let deviceType: DeviceType = DeviceType.WALLET;

let send_in_progress: any = false;
let isDispatchRunning: any = false;
const timersMap: Map<string, NodeJS.Timeout> = new Map();
const isToken = 0;

const memory = new WebAssembly.Memory({
  initial: 16777216 / 65536,
  maximum: 2147483648 / 65536,
  shared: true,
});

/* ── Disconnect event subscribers ────────────────────────────────────────── */
type VoidHandler = () => void;
const disconnectHandlers = new Set<VoidHandler>();

export function onDeviceDisconnect(handler: VoidHandler): () => void {
  disconnectHandlers.add(handler);
  return () => disconnectHandlers.delete(handler);
}

export function resetOperation(): EventEmitter {
  res.data = undefined;
  res.error = undefined;
  emitter = new EventEmitter();
  return emitter;
}

/* ── Internal helpers ────────────────────────────────────────────────────── */
export function setSelectedDevice(device: HIDDevice | undefined) {
  SELECTEDDEVICE = device;
}

export async function setDeviceAndDeviceType(device: HIDDevice) {
  SELECTEDDEVICE = device;
  await exportWASM?.crypto_guard_if_set_device_type(SELECTEDDEVICE.productId);
  deviceChipType = [0x5244, 0x5334, 0x533b].includes(SELECTEDDEVICE.productId)
    ? DeviceChipType.SINGLE_CHIP
    : DeviceChipType.DUAL_CHIP;
  deviceType =
    SELECTEDDEVICE.productId === 0x5244
      ? DeviceType.RECOVERY
      : DeviceType.WALLET;
}

export async function receivingData(data: any) {
  responseQueue.push(data.data.buffer);
}

export async function usbConnect(): Promise<void> {
  try {
    if (SELECTEDDEVICE) {
      SELECTEDDEVICE.addEventListener("inputreport", receivingData);
      navigator.hid.ondisconnect = async () => {
        await usbDisconnect();
      };
      if (!SELECTEDDEVICE.opened) {
        await SELECTEDDEVICE.open();
        SELECTEDDEVICE.addEventListener("inputreport", receivingData);
        await exportWASM?.crypto_guard_if_set_device_type(SELECTEDDEVICE.productId);
      }
      await exportWASM?.crypto_guard_if_notify(NotifyType.CONNECTED, null, 0, 0);
    } else {
      throw new Error("DeviceDisconnected");
    }
  } catch (err: any) {
    usbDisconnect();
  }
}

export async function usbDisconnect() {
  send_in_progress = false;
  if (SELECTEDDEVICE) {
    await SELECTEDDEVICE?.close();
    SELECTEDDEVICE = undefined;
  }
  await exportWASM?.crypto_guard_if_notify(NotifyType.DISCONNECTED, null, 0, 0);
  disconnectHandlers.forEach((h) => h());
}

async function usbSendDispatch(sendType: SendDataType) {
  try {
    if (SELECTEDDEVICE?.opened) {
      if (SendDataType.FEATURE_DATA === sendType) {
        await SELECTEDDEVICE.sendFeatureReport(SendBuffer[0], SendBuffer.slice(1, 64));
      } else if (SendDataType.INTERRUPT_DATA === sendType) {
        await SELECTEDDEVICE.sendReport(SendBuffer[0], SendBuffer.slice(1, 64));
      } else {
        if (send_in_progress) {
          await exportWASM?.crypto_guard_if_notify(NotifyType.SEND_STATUS, null, 0, -1);
          send_in_progress = false;
        }
      }
      await exportWASM?.crypto_guard_if_notify(NotifyType.SEND_STATUS, null, 0, 0);
    } else {
      throw new Error("NoDeviceFound");
    }
  } catch (_err: any) {
    if (SELECTEDDEVICE?.opened) {
      await exportWASM?.crypto_guard_if_notify(
        NotifyType.SEND_STATUS,
        new Uint8Array().byteOffset,
        0,
        -1,
      );
    }
  }
}

async function usbSendReport(_pu8_data: any, _u16_data_len: any, _pv: any, _data_cmd_type: any) {
  await usbSendDispatch(SendDataType.INTERRUPT_DATA);
}

async function usbSendFeatureReport(
  _pu8_data: any, _u16_data_len: any, _ps32_err: any, _pv: any, _data_cmd_type: any,
) {
  await usbSendDispatch(SendDataType.FEATURE_DATA);
}

async function usbReadFeatureReport(report_id: any) {
  try {
    if (SELECTEDDEVICE?.opened) {
      const view = await SELECTEDDEVICE.receiveFeatureReport(report_id);
      const data = new Uint8Array(view.buffer);
      readFeatureReportBuffer.set(data, 0);
      await exportWASM?.crypto_guard_if_notify(
        NotifyType.RECEIVED_I2C_USB_STATUS,
        readFeatureReportBuffer.byteOffset,
        data.length,
        0,
      );
    }
  } catch (_) {}
}

/* ── WASM callbacks ──────────────────────────────────────────────────────── */
function consoleLog(data: any) {
  const bytes = new Uint8Array(memoryBuffer.buffer, data);
  let strlen = 0;
  while (bytes[strlen] !== 0) strlen++;
  const str = new TextDecoder("utf8").decode(bytes.slice(0, strlen));
  console.log(`[Device WASM] ${str}`);
}

function onGetWalletIdResult(
  walletId: number | undefined,
  walletIdLength: number | undefined,
  error_code: number,
) {
  if (!error_code) {
    const arr = new Uint8Array(memoryBuffer.buffer, walletId, walletIdLength);
    res.data = Array.from(arr);
  }
}

export async function onGetXpubResult(
  xpub: number | undefined,
  xpubLength: number | undefined,
  error_code: any,
) {
  if (error_code) {
    res.error = deviceErrorFromCode(error_code);
  } else {
    const arr = new Uint8Array(memoryBuffer.buffer, xpub, xpubLength);
    let xpubArray = Array.from(arr);
    if (xpubArray[xpubArray.length - 1] === 0) {
      xpubArray = xpubArray.slice(0, xpubArray.length - 1);
    }
    res.data = xpubArray;
  }
  await once(emitter, "cleanUpFinished");
  emitter.emit("ResolveExtPubKey");
}

export async function onSignTxResult(
  signedTransaction: number | undefined,
  signedTransactionLength: number | undefined,
  error_code: any,
) {
  if (error_code) {
    res.error = deviceErrorFromCode(error_code);
  } else {
    const arr = new Uint8Array(memoryBuffer.buffer, signedTransaction, signedTransactionLength);
    res.data = Array.from(arr);
  }
  emitter.emit("ResolveSignTransaction");
}

async function onGetAddressResult(
  address: number | undefined,
  addressLength: number | undefined,
  error_code: any,
) {
  if (!error_code) {
    const arr = new Uint8Array(memoryBuffer.buffer, address, addressLength);
    let addressArray = Array.from(arr);
    if (addressArray[addressArray.length - 1] === 0) {
      addressArray = addressArray.slice(0, addressArray.length - 1);
    }
    res.data = addressArray;
  }
  await once(emitter, "cleanUpFinished");
  emitter.emit("ResolveAddress");
}

async function onSignMsgResult(
  signedMessage: number | undefined,
  signedMessageLength: number | undefined,
  error_code: any,
) {
  if (!error_code) {
    const arr = new Uint8Array(memoryBuffer.buffer, signedMessage, signedMessageLength);
    res.data = Array.from(arr);
  }
  emitter.emit("SignMessage");
}

let UserConfirmationCallback: {
  onUserConfirmationRequest: (req: UserConfirmationRequest) => void;
  onUserConfirmationResponse: (rsp: UserConfirmationResponse) => void;
} | null = null;

function onUserConfirmationRequested(request: number) {
  UserConfirmationCallback?.onUserConfirmationRequest(request);
}

function onUserConfirmationObtained(request: number) {
  UserConfirmationCallback?.onUserConfirmationResponse(request);
}

function onTerminate(error_code: number) {
  if (error_code !== 0) usbDisconnect();
}

function onOperationCleanUp() {
  emitter?.emit("cleanUpFinished");
}

async function onMediumBusy() {
  emitter?.emit("ResolveSignTransaction");
  emitter?.emit("ResolveExtPubKey");
  throw new Error("Device is Busy");
}

function startTimer(id: number, name: string, durationInMillSeconds: number) {
  const timer = setTimeout(() => {
    exportWASM?.crypto_guard_if_timer_fire(id);
  }, durationInMillSeconds);
  timersMap.set(name, timer);
}

function stopTimer(name: string) {
  clearTimeout(timersMap.get(name));
}

/* ── WASM initialisation ──────────────────────────────────────────────────── */
export async function initializeWasm() {
  if (SELECTEDDEVICE) {
    if (SELECTEDDEVICE.opened) {
      SELECTEDDEVICE.addEventListener("inputreport", receivingData);
    } else {
      await SELECTEDDEVICE.open();
      SELECTEDDEVICE.addEventListener("inputreport", receivingData);
    }
  }

  if (exportWASM) return;

  // webpack 5 resolves new URL(path, import.meta.url) as an asset/resource URL
  const wasmUrl = new URL("../../assets/crypto_guard_if.wasm", import.meta.url);
  const response = await fetch(wasmUrl.href);
  if (!response.ok) throw new Error(`Failed to fetch WASM module: ${response.status}`);

  const wasmBuffer = await response.arrayBuffer();
  const wasmModule = await WebAssembly.compile(wasmBuffer);

  return WebAssembly.instantiate(wasmModule, {
    env: {
      curTime: () => Date.now(),
      emscripten_resize_heap: memory.grow,
      consoleLog,
      usbConnect,
      onConnectionDone: () => {},
      usbDisconnect,
      usbSendReport,
      usbSendFeatureReport,
      usbReadFeatureReport,
      onGetWalletIdResult,
      onGetAddressResult,
      onGetXpubResult,
      onSignTxResult,
      onSignMsgResult,
      requestUserConfirmation: onUserConfirmationRequested,
      onUserConfirmation: onUserConfirmationObtained,
      onTerminate,
      onOperationCleanUp,
      notify_medium_busy: onMediumBusy,
      startTimer,
      stopTimer,
      onSuccessionPlanCreateResult: () => {},
      onSuccessionPlanCheckStatusResult: () => {},
      onSuccessionPlanSignOrderResult: () => {},
      onSuccessionPlanGetInfoResult: () => {},
      SocketConnect: () => {},
      SocketSend: () => {},
      SocketRecieve: () => {},
      SocketFree: () => {},
      SocketPoll: () => {},
      onSessionChallenge: () => {},
      onSessionChallengeRsp: () => {},
      onGenuineCheckResult: () => {},
      onOTAChunkSent: () => {},
      onOTAUpgradeDone: () => {},
      ota_req_ota_version: () => {},
      onGetFWVersion: () => {},
      onOTAUserConfirmation: () => {},
      onSuccesionForwardToGenuineCheck: () => {},
      onSuccessionPlanQueryDeviceCommandResult: () => {},
      onGetInstalledAppsInfoResult: () => {},
      onInstallAppResult: () => {},
      onUninstallAppResult: () => {},
      allocateOnMemory: () => {},
      onSignTypedDataResult: () => {},
      __assert_fail: () => {},
      wasi_snapshot_preview1: () => {},
      setTempRet0: () => {},
      _embind_register_void: () => {},
      _embind_register_bool: () => {},
      _embind_register_std_string: () => {},
      _embind_register_std_wstring: () => {},
      _embind_register_emval: () => {},
      _embind_register_integer: () => {},
      _embind_register_float: () => {},
      _embind_register_memory_view: () => {},
      _embind_register_bigint: () => {},
      __indirect_function_table: () => {},
      emscripten_memcpy_big: () => {},
      onGetSavedInstalledAppsInfoResult: () => {},
    },
  }).then(async (wasmResult) => {
    exportWASM = wasmResult.exports;
    memoryBuffer = wasmResult.exports.memory;
    exportWASM?.crypto_guard_if_get_wasm_version();
    dispatchLoop();

    const temp = exportWASM?.crypto_guard_if_malloc(1024 * 1024);
    let offset = 0;

    SendBuffer = new Uint8Array(memoryBuffer.buffer, temp + offset, 64);
    offset += SendBuffer.length;
    responseBuffer = new Uint8Array(memoryBuffer.buffer, temp + offset, 64);
    offset += responseBuffer.length;
    tokenBuffer = new Uint8Array(memoryBuffer.buffer, temp + offset, 1024);
    offset += tokenBuffer.length;
    walletIdBuffer = new Uint8Array(memoryBuffer.buffer, temp + offset, 20);
    offset += walletIdBuffer.length;
    readFeatureReportBuffer = new Uint8Array(memoryBuffer.buffer, temp + offset, 64);
    offset += readFeatureReportBuffer.length;
    pathBuffer = new Uint32Array(memoryBuffer.buffer, temp + offset, 5);
    offset += pathBuffer.length;
    txBuffer = new Uint8Array(memoryBuffer.buffer, temp + 10 * 1024 - 1, 1014 * 1024);
    responseQueue = [];
    res = { data: undefined, error: undefined };

    exportWASM?.crypto_guard_if_init(temp);
    SendBuffer.fill(0);
    responseBuffer.fill(0);
    pathBuffer.fill(0);
    txBuffer.fill(0);
    tokenBuffer.fill(0);
    walletIdBuffer.fill(0);
  });
}

async function dispatchLoop() {
  if (!isDispatchRunning) {
    isDispatchRunning = true;
    await exportWASM?.crypto_guard_if_dispatch();
    isDispatchRunning = false;
  }
  if (responseQueue?.length > 0) {
    const data = responseQueue.shift();
    notifyReceivedData(data);
  }
  setTimeout(dispatchLoop, 1);
}

async function notifyReceivedData(data: any) {
  const dataArray = Buffer.from(data);
  responseBuffer.fill(0);
  responseBuffer.set(new Uint8Array(dataArray));
  await exportWASM?.crypto_guard_if_dispatch();
  await exportWASM?.crypto_guard_if_notify(
    NotifyType.RECEIVED_DATA,
    responseBuffer.byteOffset,
    dataArray.length,
    0,
  );
}

export function setUserConfirmationCallbacks(
  onRequest: (req: UserConfirmationRequest) => void,
  onResponse: (rsp: UserConfirmationResponse) => void,
) {
  UserConfirmationCallback = {
    onUserConfirmationRequest: onRequest,
    onUserConfirmationResponse: onResponse,
  };
}
